// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * React hooks for working with an Embla carousel.
 *
 * - {@link useCarousel} initialises Embla, mirrors its state into React, and
 *   produces the value consumed by `CarouselContext`.
 * - {@link useArrowElement} computes className + disabled state for a single
 *   navigation arrow.
 */

import {
    ArrowElementProps,
    ArrowElementState,
    CarouselContextValue,
    CarouselInstance,
} from "@stoneage/App/Components/EmblaCarousel/models";
import { xClassNames } from "@stoneage/XDL/Utilities";
import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";

// ─── useCarousel ──────────────────────────────────────────────────────────────

/**
 * Initi@stoneage/App/Components/NewCarousellise an Embla carousel and expose its reactive state.
 *
 * @param options Embla options (loop, align, axis, …).
 * @param plugins Embla plugins (Autoplay, AutoHeight, …).
 * @returns
 *   - `carouselRef`   — attach to the Embla viewport element.
 *   - `carouselApi`   — the Embla API, or `null` before init.
 *   - `contextValue`  — the value to pass to `CarouselProvider`.
 */
export const useCarousel = (options: EmblaOptionsType = {}, plugins: EmblaPluginType[] = []) => {
    const [carouselRef, carouselApi = null] = useEmblaCarousel(options, plugins);

    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [canScroll, setCanScroll] = useState(true);
    const [paused, setPaused] = useState(false);

    const onPlayPause = useCallback(() => {
        if (paused) {
            carouselApi?.plugins().autoplay?.play();
        } else {
            carouselApi?.plugins().autoplay?.stop();
        }
        setPaused(!paused);
    }, [paused, carouselApi]);

    const updateState = useCallback((api: EmblaCarouselType) => {
        const prev = api.canScrollPrev();
        const next = api.canScrollNext();
        setCanScrollPrev(prev);
        setCanScrollNext(next);
        setSelectedIndex(api.selectedScrollSnap());
        setCanScroll(prev || next);
    }, []);

    useEffect(() => {
        if (!carouselApi) return;

        setScrollSnaps(carouselApi.scrollSnapList());
        updateState(carouselApi);

        carouselApi.on("select", updateState);
        carouselApi.on("reInit", updateState);

        return () => {
            carouselApi.off("select", updateState);
            carouselApi.off("reInit", updateState);
        };
    }, [carouselApi, updateState]);

    const contextValue: CarouselContextValue = {
        carouselApi,
        canScrollPrev,
        canScrollNext,
        selectedIndex,
        scrollSnaps,
        canScroll,
        direction: options.axis === "y" ? "vertical" : "horizontal",
        paused,
        onPlayPause,
    };

    return {
        carouselRef,
        carouselApi,
        contextValue,
    };
};

// ─── useArrowElement ──────────────────────────────────────────────────────────

const DEBOUNCE_MS = 50;

const buildClassName = (arrowType: ArrowElementProps["arrowType"], showOnMobile: boolean, disabled: boolean) =>
    xClassNames.append([
        `arrow-button xng-carousel-${arrowType}`,
        showOnMobile ? "arrow-on-mobile" : "arrow-on-desktop",
        disabled && "carousel-button-disabled",
    ]);

/**
 * Track the enabled/disabled state of a navigation arrow.
 *
 * The hook subscribes to the carousel's `select`, `scroll`, and `reInit`
 * events and produces the matching class name + disabled flag.
 */
export function useArrowElement({ carousel, showOnMobile = false, arrowType }: ArrowElementProps): ArrowElementState {
    const [state, setState] = useState<ArrowElementState>(() => ({
        className: buildClassName(arrowType, showOnMobile, true),
        disabled: true,
    }));

    const updateArrowState = useCallback(
        (instance: CarouselInstance) => {
            const canScroll = arrowType === "next" ? instance.canScrollNext() : instance.canScrollPrev();
            const isDisabled = !canScroll;

            setState({
                className: buildClassName(arrowType, showOnMobile, isDisabled),
                disabled: isDisabled,
            });
        },
        [arrowType, showOnMobile],
    );

    // `useMemo` here is intentional: `useCallback(debounce(...))` would create
    // a fresh debounced function every render, defeating the debounce.
    const debouncedUpdate = useMemo(
        () => debounce((instance: CarouselInstance) => updateArrowState(instance), DEBOUNCE_MS),
        [updateArrowState],
    );

    // Cancel any pending debounced call when the debouncer changes or unmounts.
    useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

    useEffect(() => {
        if (!carousel) {
            setState({
                className: buildClassName(arrowType, showOnMobile, true),
                disabled: true,
            });
            return;
        }

        updateArrowState(carousel);

        const handleSelect = () => updateArrowState(carousel);
        const handleScroll = () => debouncedUpdate(carousel);
        const handleReInit = () => updateArrowState(carousel);

        carousel.on("select", handleSelect);
        carousel.on("scroll", handleScroll);
        carousel.on("reInit", handleReInit);

        return () => {
            carousel.off("select", handleSelect);
            carousel.off("scroll", handleScroll);
            carousel.off("reInit", handleReInit);
        };
    }, [carousel, arrowType, showOnMobile, updateArrowState, debouncedUpdate]);

    return state;
}
